import React, { useState, useCallback, useEffect } from "react";
// Adjust the import paths based on your project structure
import {
  availableEtlProviders,
  browseForEtlProvidersPipelines,
} from "../../../../../api/etlProviderQueries";
import useETLProviderPipelineBrowseNotification from "../../../../../Hooks/useETLProviderPipelineBrowseNotification";
import { useApi } from "../../../../../api/useApi";
import { sortByAlpha } from "../../../../../common/helpers/util";
import SplashLoader from "../../../../Loaders/SplashLoader";
import ErrorMessages from "../../../../Notifications/ErrorMessages";
import {
  FormControl,
  StyledSelect,
  Label,
} from "../../../../Form/FormControls";

const EtlPipelineSelector = ({ nodeData, setNodeData, usedEtlPipelines }) => {
  const createDisabledOptions = (options, usedEntities, idKey = "value") => {
    return options.map((option) => ({
      ...option,
      isDisabled: usedEntities.find((e) => e.name === option[idKey]),
    }));
  };

  const [noProvidersMessage, setNoProvidersMessage] = useState("");
  const [etlProviderInstanceId, setEtlProviderInstanceId] = useState(
    nodeData.etlProviderInstanceId ?? null
  );

  // Providers State
  const [providers, setProviders] = useState([]);

  // Instance Names State
  const [instanceNames, setInstanceNames] = useState([]);
  const [selectedInstanceName, setSelectedInstanceName] = useState(
    nodeData.name ?? ""
  );

  // Load Available Providers
  const [{ errors, data }] = useApi(availableEtlProviders);

  // Browse API Call
  const [{ errors: browseDataRequestErrors }, browse] = useApi();

  // Set locally browsed failure
  const [browseFailures, setBrowseFailures] = useState();

  // Handle Browse Request
  const browseRequest = useCallback(
    (browseObject) => {
      browse({
        query: browseForEtlProvidersPipelines,
        variables: {
          browseInfo: { etlProviderInstanceId: browseObject },
        },
      });
    },
    [browse]
  );

  // Provider Browse Hook
  const { etlProviderPipelineBrowseCompleted, setEtlProviderBrowseCompleted } =
    useETLProviderPipelineBrowseNotification();

  // Handle Browse Results
  useEffect(() => {
    if (etlProviderPipelineBrowseCompleted) {
      const response = etlProviderPipelineBrowseCompleted.payload;
      if (response?.ErrorMessage) {
        setBrowseFailures([{ message: response?.ErrorMessage }]);
        setEtlProviderBrowseCompleted(null);
      } else {
        const items = response.BrowseItems.map((item) => {
          return {
            label: item.Name,
            value: item.Name,
          };
        });

        const sortedItems = items?.length ? sortByAlpha(items, "label") : [];

        setInstanceNames(sortedItems);
        setEtlProviderBrowseCompleted(null);
      }
    }
  }, [
    etlProviderPipelineBrowseCompleted,
    setInstanceNames,
    setEtlProviderBrowseCompleted,
    setBrowseFailures,
  ]);

  // Provider Handler
  useEffect(() => {
    if (data && data?.availableEtlProviders?.edges?.length) {
      const providers =
        data?.availableEtlProviders?.edges?.[0]?.node?.instances
          .filter((instance) => instance.enabled)
          .map((inst) => {
            return {
              label: inst.name,
              value: inst.id,
            };
          }) ?? [];

      setProviders(providers);
    } else if (data && !data?.availableEtlProviders?.edges?.length) {
      setNoProvidersMessage(
        "You have not configured an ETL Provider within the admin ETL settings."
      );
    }
  }, [data, setProviders]);

  useEffect(() => {
    if (etlProviderInstanceId) {
      browseRequest(etlProviderInstanceId);
    }
  }, [etlProviderInstanceId, browseRequest]);

  if (noProvidersMessage) {
    return <ErrorMessages errors={[{ message: noProvidersMessage }]} />;
  }

  if (!providers) {
    return <SplashLoader text={"Preparing Providers"} />;
  }

  if (browseDataRequestErrors?.length) {
    return <ErrorMessages errors={browseDataRequestErrors} />;
  }

  if (browseFailures?.length) {
    return <ErrorMessages errors={browseFailures} />;
  }

  if (errors?.length) {
    return <ErrorMessages errors={errors} />;
  }

  // Create disabled options for ETL Pipelines
  const InstanceOptionsWithDisabled = createDisabledOptions(
    instanceNames,
    usedEtlPipelines
  );

  return (
    <div style={{ padding: ".4rem" }}>
      <div
        style={{
          flex: 1,
          marginLeft: "auto",
          marginRight: "1rem",
        }}
      >
        <FormControl>
          <Label>ETL Provider</Label>
          <StyledSelect
            className={`react-select-container`}
            classNamePrefix={`react-select`}
            name={`etlProviderInstanceId`}
            id={`etlProviderInstanceId`}
            inputId={`etlProviderInstanceIdSelect-input`}
            instanceId={`etlProviderInstanceIdSelect-instance`}
            label="Instance"
            options={providers}
            placeholder={`Select Instance`}
            value={
              providers?.find((p) => p.value === etlProviderInstanceId) ?? null
            }
            menuPortalTarget={document.body}
            menuPlacement="auto"
            onChange={(e) => {
              browseRequest(e?.value);
              setEtlProviderInstanceId(e?.value);
              setNodeData((prev) => ({
                ...prev,
                etlProviderInstanceId: e?.value,
              }));
            }}
          />
        </FormControl>
        {etlProviderInstanceId && (
          <FormControl>
            <Label>ETL Provider Instance</Label>
            <StyledSelect
              className={`react-select-container`}
              classNamePrefix={`react-select`}
              name={`etlProviderInstanceName`}
              id={`etlProviderInstanceName`}
              inputId={`etlProviderInstanceNameSelect-input`}
              instanceId={`etlProviderInstanceNameSelect-instance`}
              label="Instance"
              options={InstanceOptionsWithDisabled}
              placeholder={`Select Instance`}
              value={
                instanceNames?.find(
                  (isn) => isn?.value === selectedInstanceName
                ) ?? null
              }
              menuPortalTarget={document.body}
              menuPlacement="auto"
              onChange={(e) => {
                setSelectedInstanceName(e?.value);
                setNodeData((prev) => ({
                  ...prev,
                  name: e?.value,
                  entityName: e?.label,
                }));
              }}
            />
          </FormControl>
        )}
      </div>
    </div>
  );
};

export default EtlPipelineSelector;
